import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck, RefreshCw, Unplug, Video, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui-bits";
import { toast } from "sonner";
import { fmtDate, fmtTime, fmtRelative } from "@/lib/constants";

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#f25022" d="M1 1h10v10H1z" />
      <path fill="#7fba00" d="M12 1h10v10H12z" />
      <path fill="#00a4ef" d="M1 12h10v10H1z" />
      <path fill="#ffb900" d="M12 12h10v10H12z" />
    </svg>
  );
}

const PROVIDERS = {
  google: {
    prefix: "gcal", param: "gcal", Icon: GoogleIcon,
    statusUrl: "/expert/calendar/google", authorizeUrl: "/oauth/calendar/authorize",
    syncUrl: "/expert/calendar/google/sync", disconnectUrl: "/expert/calendar/google",
  },
  microsoft: {
    prefix: "mscal", param: "mscal", Icon: MicrosoftIcon,
    statusUrl: "/expert/calendar/microsoft", authorizeUrl: "/oauth/calendar/ms/authorize",
    syncUrl: "/expert/calendar/microsoft/sync", disconnectUrl: "/expert/calendar/microsoft",
  },
};

export function CalendarOAuthConnect({ provider = "google", onBusyChange }) {
  const cfg = PROVIDERS[provider];
  const { prefix, param, Icon } = cfg;
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);

  const load = useCallback(() => {
    api.get(cfg.statusUrl).then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false, connected: false }));
  }, [cfg.statusUrl]);
  useEffect(() => { load(); }, [load]);

  // Refresh the "last synced X min ago" label every minute
  useEffect(() => { const id = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(id); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get(param);
    if (g === "connected") { toast.success(t(`${prefix}.toastConnected`)); onBusyChange?.(); }
    else if (g === "error") { toast.error(t(`${prefix}.toastError`)); }
    if (g) {
      params.delete(param);
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get(cfg.authorizeUrl); window.location.href = data.authorization_url; }
    catch (err) { toast.error(apiError(err)); setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try { const { data } = await api.post(cfg.syncUrl); setStatus((s) => ({ ...s, ...data })); toast.success(t(`${prefix}.toastSynced`, { count: data.busy_count })); onBusyChange?.(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.delete(cfg.disconnectUrl); toast.success(t(`${prefix}.toastDisconnected`)); load(); onBusyChange?.(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (status === null) return <div className="card-milled rounded-[0.8rem] p-6 grid place-items-center"><Spinner className="text-primary" /></div>;
  if (!status.configured) return null;

  return (
    <div data-testid={`${param}-connect`} className="card-milled rounded-[0.8rem] p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><Video size={17} strokeWidth={1.75} /></span>
          <div>
            <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">{t(`${prefix}.title`)}</h3>
            <p className="text-sm text-muted-foreground">{t(`${prefix}.desc`)}</p>
          </div>
        </div>
        {!status.connected ? (
          <Button data-testid={`${param}-connect-btn`} onClick={connect} disabled={busy} className="rounded-lg gap-2 shrink-0 bg-card text-foreground border border-border hover:bg-secondary/60">
            {busy ? <Spinner /> : <><Icon /> {t(`${prefix}.connectBtn`)}</>}
          </Button>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <Button data-testid={`${param}-sync-btn`} onClick={sync} disabled={busy} variant="outline" className="rounded-lg gap-1.5 bg-card">{busy ? <Spinner /> : <RefreshCw size={15} />} {t(`${prefix}.syncNow`)}</Button>
            <Button data-testid={`${param}-disconnect-btn`} onClick={disconnect} disabled={busy} variant="outline" className="rounded-lg gap-1.5 bg-card text-destructive hover:text-destructive"><Unplug size={15} /> {t(`${prefix}.disconnect`)}</Button>
          </div>
        )}
      </div>

      {status.connected ? (
        <div className="mt-4 rounded-xl border border-success/30 bg-success/5 p-4">
          <p className="text-sm font-medium inline-flex items-center gap-2"><CalendarCheck size={16} className="text-success" /> {t(`${prefix}.connectedAs`, { email: status.email })}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t(`${prefix}.busyBlocks`, { count: status.busy_count })}
            {status.last_sync ? (
              <> · <span title={`${fmtDate(status.last_sync)} ${fmtTime(status.last_sync)}`}>{t(`${prefix}.lastSyncRel`, { rel: fmtRelative(status.last_sync, i18n.language) })}</span></>
            ) : ""}
          </p>
          <p className="text-xs text-primary mt-2 inline-flex items-center gap-1.5"><Video size={13} /> {t(`${prefix}.meetNote`)}</p>
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1.5"><Check size={13} className="text-success" /> {t(`${prefix}.privacyNote`)}</p>
      )}
    </div>
  );
}
